import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, InputAdornment, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { OrganisationIAutoCompleteItem, OrganisationSelector } from '../../selectors/OrganisationSelector';
import { FieldSwitchButton } from '../components/FieldSwitchButton';
import BusinessIcon from '@material-ui/icons/Business';
import { TranslationTextList } from '../../../locales/common';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OrganisationPanel');

// Explicit request (EnquiryFormDrawer split): the drawer's "Organisation" accordion, moved out of
// EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription / handler logic
// unchanged. Same pattern as ContactDetailsPanel: a FormSpy hands the memoized body only the few
// values it displays (as primitives), and event handlers read the form's values at the moment the
// event fires - exactly what the render-time `values` closure held before.
export interface OrganisationPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    translate: (key: keyof TranslationTextList) => string;
    isSourceTypeClientOnly: true | undefined;
    isExpanded: boolean;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    onDropDownSelect: (
        form: FormApi<Enquiry>,
        values: AnyObject,
        input: FieldInputProps<any, HTMLElement>, // tslint:disable-line: no-any
        selection: any, // tslint:disable-line: no-any
        name: string,
    ) => void;
}

interface OrganisationPanelBodyProps extends OrganisationPanelProps {
    organisationPanelContent: string;
    isOrganisationSwitched: boolean;
    isOrganisationLockedToContact: boolean;
}

const OrganisationPanelBody: React.FC<OrganisationPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, translate, isSourceTypeClientOnly, isExpanded, onExpandChange, onSwitch, onDropDownSelect,
        organisationPanelContent, isOrganisationSwitched, isOrganisationLockedToContact,
    } = bodyProps;

    return (

        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="organisation-content"
                id="organisation-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipPurple)}>
                        <BusinessIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelPurple, classes.sectionLabelText)}>
                        {translate('Organisation')}
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {organisationPanelContent}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.toggleFieldWrapepr}>
                            <div className="field">
                                {
                                    isOrganisationSwitched ? (
                                        <Field
                                            name="organisationSelector"
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <OrganisationSelector
                                                    variant="outlined"
                                                    size="small"
                                                    {...input}
                                                    label={translate('Organisation Name')}
                                                    error={meta.error && meta.touched}
                                                    helperText={
                                                        meta.error &&
                                                            meta.touched
                                                            ? `${translate('Organisation Name')} is required`
                                                            : ''
                                                    }
                                                    onSelection={(selection: OrganisationIAutoCompleteItem, name: string) => {
                                                        const values = form.getState().values;
                                                        input.onChange(selection);
                                                        onDropDownSelect(form, values, input, selection, name);
                                                    }}
                                                    isSourceTypeClientOnly={isSourceTypeClientOnly}
                                                    endAdornmentExtra={(
                                                        <FieldSwitchButton
                                                            tooltip="Switch to enter a new organisation name"
                                                            onClick={() => onSwitch(form, form.getState().values, 'organisation')}
                                                        />
                                                    )}
                                                // value={input.value}
                                                />
                                            )}
                                        </Field>
                                    ) :
                                        (
                                            <Field
                                                name="organisation"
                                                // validate={required}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...input}
                                                        label="Organisation Name"
                                                        type="text"
                                                        // required={true}
                                                        className={classes.textField}
                                                        disabled={isOrganisationLockedToContact}
                                                        error={meta.error && meta.touched}
                                                        autoComplete="abcd"
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Name is required'
                                                                : ''
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FieldSwitchButton
                                                                        tooltip="Switch to select an existing organisation"
                                                                        onClick={() => onSwitch(form, form.getState().values, 'organisation')}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )
                                }
                            </div>
                        </div>
                    </Grid>
                    <Grid
                        item={true}
                        xs={12}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="positionAtOrganisation"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Position/Title"
                                    type="text"
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Position is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OrganisationPanelBody.displayName = 'OrganisationPanelBody';

export const OrganisationPanel: React.FC<OrganisationPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => {
            const organisationPanelContent = `${values.organisation ? values.organisation : ''}${values.organisation && values.positionAtOrganisation ? `, ${values.positionAtOrganisation}` : values.positionAtOrganisation ? values.positionAtOrganisation : ''}`;

            return (
                <OrganisationPanelBody
                    {...panelProps}
                    organisationPanelContent={organisationPanelContent}
                    isOrganisationSwitched={!!values.isOrganisationSwitched}
                    isOrganisationLockedToContact={!!values.organisationGuid}
                />
            );
        }}
    </FormSpy>
), arePropsEqualIgnoringForm);
OrganisationPanel.displayName = 'OrganisationPanel';
